import * as Highcharts from 'highcharts';
import {
  REPORT_CHART_COST,
  REPORT_CHART_DEFAULT_HEIGHT,
  REPORT_CHART_FONT_SIZE,
  REPORT_CHART_GEOMETRY_DECIMALS,
  REPORT_CHART_LIMITS,
  REPORT_CHART_WIDTH,
  reportChartIdPrefix,
} from '../constants/reportCharts';
import { reportChartLabel, reportLabels } from '../constants/reportLabels';
import type { ChartSubmission, ReportChartId, ReportPackageKey } from '../types';
import { GEOMETRY_ATTRIBUTES, gradientExtentOk, keptAttributes, referenceFallback } from './svgAttributes';
import { splitClosedArcs } from './svgArcs';

const SVG_NS = 'http://www.w3.org/2000/svg';
// Mirror of the server rebuild: the elements it keeps (anything else is dropped with its subtree, and so is a nested
// svg), the shapes (no child kept), the content kept inside each resource and the references it resolves.
const KEPT_ELEMENTS = new Set([
  'g',
  'defs',
  'path',
  'rect',
  'circle',
  'ellipse',
  'line',
  'polyline',
  'polygon',
  'text',
  'tspan',
  'clipPath',
  'linearGradient',
  'radialGradient',
  'stop',
  'marker',
]);
const TEXT_ELEMENTS = new Set(['text', 'tspan']);
const SHAPE_ELEMENTS = new Set(['path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon']);
const LEAF_ELEMENTS = new Set([...SHAPE_ELEMENTS, 'stop']);
const RESOURCE_CONTENT: Record<string, Set<string>> = {
  marker: new Set([...SHAPE_ELEMENTS, 'g']),
  clipPath: new Set([...SHAPE_ELEMENTS, 'g']),
  linearGradient: new Set(['stop']),
  radialGradient: new Set(['stop']),
};
const GRADIENTS = ['linearGradient', 'radialGradient'];
// The attributes left by the server cleaning only hold the references their element may draw.
const REFERENCE_TARGETS: [string, string[]][] = [
  ['clip-path', ['clipPath']],
  ['marker-start', ['marker']],
  ['marker-end', ['marker']],
  ['fill', GRADIENTS],
  ['stroke', GRADIENTS],
];
const REFERENCE = /url\(\s*#([A-Za-z_][A-Za-z0-9_.-]{0,127})\s*\)/;
const DENSE_PATH_COMMAND = /[AaHhVv]/;
const FORBIDDEN_MARKUP = /<!DOCTYPE|<!ENTITY/i;
const START_TAG = /<[a-zA-Z][^<>]*>/g;
const TAG_NAME = /^<([^\s/>]+)/;
const ATTRIBUTE = /\s([^\s="'<>/]+)="([^"]*)"/g;
const PATH_DATA = /\sd="([^"]*)"/g;
const ROUNDED_ATTRIBUTES = /\s(d|points)="([^"]*)"/g;
const SIGNED_NUMBER = /[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g;
const NUMBER = '[-+]?(?:\\d+\\.?\\d*|\\.\\d+)(?:[eE][-+]?\\d+)?';
const ARC = new RegExp(`A\\s*(${NUMBER})[\\s,]+(${NUMBER})[\\s,]+${NUMBER}[\\s,]+[01][\\s,]*[01][\\s,]*(${NUMBER})[\\s,]+(${NUMBER})`, 'g');

export interface ChartJob {
  package: ReportPackageKey;
  chartId: ReportChartId;
  inputHash: string;
  /** Options of the chart, `null` when its data is missing. Built lazily, one chart per task. */
  build: () => Highcharts.Options | null;
}

export interface SvgStats {
  bytes: number;
  /** Elements of the document, as parsed by the server. */
  elements: number;
  depth: number;
  /** Elements, texts and text characters left once the server dropped the unsupported elements. */
  keptElements: number;
  keptTexts: number;
  textCharacters: number;
  longestText: number;
  /** A kept marker, clip path or gradient has more descendants than the server accepts. */
  oversizedResource: boolean;
  /** Longest `d` or `points` of a kept element; one over the server cap rejects the chart. */
  longestGeometry: number;
  oversizedGeometry: boolean;
  /** Server render cost: weights of the kept elements and of the resource descendants drawn through references, text. */
  renderCost: number;
  /** Kept paths whose `d` the server drops (unreadable or out of range): they draw nothing. */
  droppedPaths: number;
}

/** A kept element, with the resource it is drawn in (whose content the server filters), if any. */
interface KeptNode {
  element: Element;
  resource: Element | null;
  /** Index of the parent among the kept elements, -1 for the root. */
  parent: number;
}

/** A kept element with the attributes the server keeps on it. */
interface CleanNode extends KeptNode {
  attributes: Map<string, string>;
}

interface ResourceSize {
  descendants: number;
  weight: number;
}

/** A reference the server resolves: the element holding it, its attribute and the resource it draws. */
interface Reference {
  element: Element;
  name: string;
  target: Element;
}

export interface CapturedChart {
  submission: ChartSubmission;
  stats: SvgStats;
}

export interface CaptureResult {
  charts: CapturedChart[];
  warnings: string[];
}

interface CaptureOptions {
  signal: AbortSignal;
  onProgress: (done: number, total: number) => void;
}

const utf8Bytes = (text: string) => new TextEncoder().encode(text).length;

const nextTask = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

/** Screen options turned into a static, light, fixed-size drawing: no animation, boost, credits nor export menu. */
export const exportChartOptions = (options: Highcharts.Options): Highcharts.Options => {
  const height = Number(options.chart?.height) || REPORT_CHART_DEFAULT_HEIGHT;
  return Highcharts.merge(options, {
    chart: {
      width: REPORT_CHART_WIDTH,
      height,
      animation: false,
      styledMode: false,
      style: { fontSize: REPORT_CHART_FONT_SIZE },
    },
    accessibility: { enabled: false },
    // The boost module draws on a canvas, which an SVG cannot hold.
    boost: { enabled: false },
    credits: { enabled: false },
    exporting: { enabled: false },
    // Rounded corners are drawn with arcs, which the PDF renderer mishandles for empty columns and slices;
    // a text outline is a stroked copy of the label, which it renders as a blot.
    plotOptions: {
      series: { animation: false, dataLabels: { style: { textOutline: 'none' } } },
      column: { borderRadius: 0 },
      pie: { borderRadius: 0 },
    },
  });
};

/**
 * Highcharts' export turns an `rgba()` fill or stroke into `rgb()` plus an opacity attribute, even on an element that
 * already has one: the duplicate makes the XML invalid. Opacities are multiplied, as the browser combines them;
 * for any other duplicate the first value is kept.
 */
export const mergeDuplicateAttributes = (svg: string): string =>
  svg.replace(START_TAG, (tag) => {
    const attributes = [...tag.matchAll(ATTRIBUTE)];
    if (new Set(attributes.map(([, name]) => name)).size === attributes.length) return tag;
    const merged = new Map<string, string>();
    attributes.forEach(([, name, value]) => {
      const previous = merged.get(name);
      const product = Number(previous) * Number(value);
      if (previous === undefined) merged.set(name, value);
      else if (name.endsWith('opacity') && Number.isFinite(product)) merged.set(name, String(product));
    });
    const rebuilt = [...merged].map(([name, value]) => ` ${name}="${value}"`).join('');
    return `<${TAG_NAME.exec(tag)?.[1]}${rebuilt}${tag.endsWith('/>') ? '/>' : '>'}`;
  });

/** A zero-radius arc is a straight line (SVG path rules), but the PDF renderer draws it wrongly: it is written as one. */
export const straightenZeroRadiusArcs = (svg: string): string =>
  svg.replace(PATH_DATA, (attribute: string, d: string) => {
    const straightened = d.replace(ARC, (arc, rx, ry, x, y) => (Number(rx) === 0 || Number(ry) === 0 ? `L ${x} ${y}` : arc));
    return straightened === d ? attribute : ` d="${straightened}"`;
  });

const roundNumbers = (value: string, factor: number) =>
  value.replace(SIGNED_NUMBER, (token: string, offset: number) => {
    const rounded = String(Math.round(Number(token) * factor) / factor);
    // A number that lost its decimal point must stay apart from the next one (`1.96.5` is `1.96 .5`).
    const separated = /[\d.]/.test(value[offset - 1] ?? '') && !rounded.startsWith('-');
    return separated ? ` ${rounded}` : rounded;
  });

/** Path data and point lists rounded: full precision more than doubles the size and cost of a series, for nothing visible. */
export const roundGeometry = (svg: string, decimals = REPORT_CHART_GEOMETRY_DECIMALS): string =>
  svg.replace(START_TAG, (tag) =>
    tag.replace(
      ROUNDED_ATTRIBUTES,
      (_attribute: string, name: string, value: string) => ` ${name}="${roundNumbers(value, 10 ** decimals)}"`,
    ),
  );

/**
 * Draws the chart in a hidden container attached to the page (text is only measured in a rendered document),
 * returns its SVG and always destroys the chart and removes the container.
 */
export const renderChartSvg = (options: Highcharts.Options): string => {
  const container = document.createElement('div');
  container.setAttribute('aria-hidden', 'true');
  Object.assign(container.style, {
    position: 'absolute',
    left: '-10000px',
    top: '0',
    width: `${REPORT_CHART_WIDTH}px`,
    overflow: 'hidden',
    pointerEvents: 'none',
  });
  document.body.appendChild(container);
  let chart: Highcharts.Chart | undefined;
  try {
    chart = Highcharts.chart(container, exportChartOptions(options));
    return straightenZeroRadiusArcs(roundGeometry(splitClosedArcs(mergeDuplicateAttributes(chart.getSVG()))));
  } finally {
    chart?.destroy();
    container.remove();
  }
};

const isKeptChild = (child: Element, resource: Element | null) =>
  child.namespaceURI === SVG_NS &&
  KEPT_ELEMENTS.has(child.localName) &&
  (resource === null || RESOURCE_CONTENT[resource.localName].has(child.localName));

/** Elements the server keeps, in document order (its id resolution keeps the first of duplicate ids). */
const keptTree = (root: Element): KeptNode[] => {
  const kept: KeptNode[] = [];
  const stack: KeptNode[] = [{ element: root, resource: null, parent: -1 }];
  while (stack.length) {
    const node = stack.pop()!;
    const parent = kept.push(node) - 1;
    const name = node.element.localName;
    if (LEAF_ELEMENTS.has(name)) continue;
    const resource = node.resource ?? (RESOURCE_CONTENT[name] ? node.element : null);
    const children = Array.from(node.element.children).filter((child) => isKeptChild(child, resource));
    children.reverse().forEach((element) => stack.push({ element, resource, parent }));
  }
  return kept;
};

/** Element count and depth of the whole document, as the server parser counts them. */
const documentSize = (root: Element) => {
  let elements = 0;
  let depth = 0;
  const stack: [Element, number][] = [[root, 1]];
  while (stack.length) {
    const [element, level] = stack.pop()!;
    elements += 1;
    depth = Math.max(depth, level);
    Array.from(element.children).forEach((child) => stack.push([child, level + 1]));
  }
  return { elements, depth };
};

/** Text a text element holds itself, including around dropped children. */
const ownText = (element: Element) =>
  Array.from(element.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE || node.nodeType === Node.CDATA_SECTION_NODE)
    .map((node) => node.textContent ?? '')
    .join('');

/** Code points (as the server counts them) of a text and its weighted width, complex characters counting more. */
const textSize = (text: string) => {
  const codePoints = Array.from(text);
  const complex = codePoints.filter((c) => c.codePointAt(0)! > REPORT_CHART_COST.simpleCharacterMaxCodePoint).length;
  return { length: codePoints.length, width: codePoints.length + complex * (REPORT_CHART_COST.complexCharacterWeight - 1) };
};

const measureTexts = (kept: KeptNode[]) => {
  const sizes = kept.filter(({ element }) => TEXT_ELEMENTS.has(element.localName)).map(({ element }) => textSize(ownText(element)));
  return {
    keptTexts: sizes.length,
    textCharacters: sizes.reduce((sum, { length }) => sum + length, 0),
    textWidth: sizes.reduce((sum, { width }) => sum + width, 0),
    longestText: sizes.reduce((longest, { length }) => Math.max(longest, length), 0),
  };
};

const isOpacityGroup = (attributes: Map<string, string>) => (attributes.get('opacity') ?? '1') !== '1';

/** Weight of the passes of the renderer over a `d` or `points`, in integers as the server computes it. */
const geometryWeight = (name: string, value: string | undefined, passes: number) => {
  if (value === undefined) return 0;
  const { attributeCharactersPerUnit, geometryLinearLength } = REPORT_CHART_COST;
  const length = value.length * (name === 'd' && DENSE_PATH_COMMAND.test(value) ? 2 : 1);
  const perPass = Math.ceil(length / attributeCharactersPerUnit) * Math.max(length, geometryLinearLength);
  return Math.ceil((passes * perPass) / geometryLinearLength);
};

/**
 * Weight of an element alone, as the server counts it: one, plus its attribute characters (names only for `d` and
 * `points`), its opacity group (any value but `1`) over its descendants and the passes over its geometry.
 */
const elementWeight = (attributes: Map<string, string>, passes: number, descendants: number) => {
  let characters = 0;
  attributes.forEach((value, name) => (characters += name.length + (GEOMETRY_ATTRIBUTES.includes(name) ? 0 : value.length)));
  const group = isOpacityGroup(attributes)
    ? REPORT_CHART_COST.opacityGroupExtra + Math.ceil(descendants / REPORT_CHART_COST.opacityGroupDescendantsPerUnit)
    : 0;
  const geometry = GEOMETRY_ATTRIBUTES.reduce((sum, name) => sum + geometryWeight(name, attributes.get(name), passes), 0);
  return 1 + Math.ceil(characters / REPORT_CHART_COST.attributeCharactersPerUnit) + group + geometry;
};

/** Opacity groups of each kept element and its ancestors, the root and resources included. */
const opacityGroups = (kept: CleanNode[]) => {
  const groups: number[] = [];
  kept.forEach(({ parent, attributes }) => groups.push((parent < 0 ? 0 : groups[parent]) + (isOpacityGroup(attributes) ? 1 : 0)));
  return groups;
};

/** Kept descendants of each kept element (parents come first in document order). */
const descendantCounts = (kept: KeptNode[]) => {
  const counts = kept.map(() => 0);
  for (let index = kept.length - 1; index > 0; index -= 1) counts[kept[index].parent] += counts[index] + 1;
  return counts;
};

/** Gradients each element is painted with, through its resolved `fill` and `stroke`. */
const gradientPaints = (references: Reference[]) => {
  const paints = new Map<Element, number>();
  references
    .filter(({ name }) => name === 'fill' || name === 'stroke')
    .forEach(({ element }) => paints.set(element, (paints.get(element) ?? 0) + 1));
  return paints;
};

/** Kept descendants of each resource and their summed weight, the cost of one draw of it. */
const resourceSizes = (kept: KeptNode[], weights: Map<Element, number>) => {
  const sizes = new Map<Element, ResourceSize>();
  kept.forEach(({ element, resource }) => {
    if (!resource && RESOURCE_CONTENT[element.localName]) sizes.set(element, { descendants: 0, weight: 0 });
    const size = resource ? sizes.get(resource) : undefined;
    if (!size) return;
    size.descendants += 1;
    size.weight += weights.get(element) ?? 0;
  });
  return sizes;
};

/**
 * Prefixes the ids as the server does (the first of duplicate ids wins, the others are dropped) and resolves the
 * references to a resource of the expected kind; any other is reduced to its fallback colour, or dropped.
 */
const resolveReferences = (kept: CleanNode[], idPrefix: string): Reference[] => {
  const targets = new Map<string, Element>();
  kept.forEach(({ element, attributes }) => {
    const id = attributes.get('id');
    if (id === undefined) return;
    if (targets.has(id)) {
      attributes.delete('id');
    } else {
      targets.set(id, element);
      attributes.set('id', idPrefix + id);
    }
  });
  return kept.flatMap(({ element, attributes }) =>
    REFERENCE_TARGETS.flatMap(([name, kinds]): Reference[] => {
      const value = attributes.get(name) ?? '';
      const match = REFERENCE.exec(value);
      if (!match) return [];
      const target = targets.get(match[1]);
      if (target && kinds.includes(target.localName)) {
        attributes.set(name, `url(#${idPrefix}${match[1]})${value.slice(match.index + match[0].length)}`);
        return [{ element, name, target }];
      }
      const fallback = referenceFallback(value);
      if (fallback) attributes.set(name, fallback);
      else attributes.delete(name);
      return [];
    }),
  );
};

/** Longest `d` or `points` as submitted, and whether one exceeds its server cap (far lower inside a resource). */
const measureGeometry = (kept: KeptNode[]) =>
  kept.reduce(
    (stats, { element, resource }) => {
      const cap = resource ? REPORT_CHART_LIMITS.maxSvgResourceGeometryLength : REPORT_CHART_LIMITS.maxSvgGeometryLength;
      const length = Math.max(0, ...GEOMETRY_ATTRIBUTES.map((name) => element.getAttribute(name)?.length ?? 0));
      return { longestGeometry: Math.max(stats.longestGeometry, length), oversizedGeometry: stats.oversizedGeometry || length > cap };
    },
    { longestGeometry: 0, oversizedGeometry: false },
  );

/**
 * Size, counts and render cost of an SVG as the server will measure them, its ids prefixed with `idPrefix`; `null` when
 * it is not a well-formed SVG.
 */
export const measureSvg = (svg: string, idPrefix = ''): SvgStats | null => {
  if (FORBIDDEN_MARKUP.test(svg)) return null;
  const root = new DOMParser().parseFromString(svg, 'image/svg+xml').documentElement;
  if (root.namespaceURI !== SVG_NS || root.localName !== 'svg' || root.getElementsByTagName('parsererror').length) return null;
  const kept: CleanNode[] = keptTree(root).map((node) => ({ ...node, attributes: keptAttributes(node.element, node.resource !== null) }));
  // A gradient too short to be drawn loses its id, hence its references, before anything is weighed.
  kept.forEach(({ element, attributes }) => {
    if (GRADIENTS.includes(element.localName) && !gradientExtentOk(element.localName, attributes)) attributes.delete('id');
  });
  const references = resolveReferences(kept, idPrefix);
  const groups = opacityGroups(kept);
  const descendants = descendantCounts(kept);
  const paints = gradientPaints(references);
  const weights = new Map(
    kept.map(({ element, attributes }, index) => {
      const passes = 1 + groups[index] + (paints.get(element) ?? 0);
      return [element, elementWeight(attributes, passes, descendants[index])];
    }),
  );
  const { textWidth, ...texts } = measureTexts(kept);
  const sizes = resourceSizes(kept, weights);
  const textCost = REPORT_CHART_COST.textElementExtra * texts.keptTexts + Math.ceil(textWidth / REPORT_CHART_COST.textCharactersPerUnit);
  const elementsCost = [...weights.values()].reduce((sum, weight) => sum + weight, 0);
  // Each reference draws its resource once more.
  const resourcesCost = references.reduce((sum, { target }) => sum + (sizes.get(target)?.weight ?? 0), 0);
  return {
    bytes: utf8Bytes(svg),
    ...documentSize(root),
    keptElements: kept.length,
    ...texts,
    ...measureGeometry(kept),
    oversizedResource: [...sizes].some(
      ([{ localName }, size]) => size.descendants > (REPORT_CHART_LIMITS.maxResourceDescendants[localName] ?? 0),
    ),
    renderCost: elementsCost + textCost + resourcesCost,
    droppedPaths: kept.filter(
      ({ element, attributes }) => element.localName === 'path' && element.getAttributeNS(null, 'd') !== null && !attributes.has('d'),
    ).length,
  };
};

const exceedsSvgLimits = (stats: SvgStats) =>
  stats.bytes > REPORT_CHART_LIMITS.maxSvgBytes ||
  stats.elements > REPORT_CHART_LIMITS.maxSvgElements ||
  stats.depth > REPORT_CHART_LIMITS.maxSvgDepth ||
  stats.keptTexts > REPORT_CHART_LIMITS.maxSvgTexts ||
  stats.textCharacters > REPORT_CHART_LIMITS.maxSvgTextCharacters ||
  stats.longestText > REPORT_CHART_LIMITS.maxSvgTextElementCharacters ||
  stats.oversizedResource ||
  stats.oversizedGeometry ||
  stats.renderCost > REPORT_CHART_LIMITS.maxReportRenderCost;

const throwIfAborted = (signal: AbortSignal) => {
  if (signal.aborted) throw new DOMException('Chart export cancelled', 'AbortError');
};

/**
 * Renders every chart in its own task so the page stays responsive; a chart without data, failing to render or
 * over the server limits is left out with a warning. Rejects with an `AbortError` once the signal is aborted.
 */
export const captureReportCharts = async (jobs: ChartJob[], { signal, onProgress }: CaptureOptions): Promise<CaptureResult> => {
  const charts: CapturedChart[] = [];
  const warnings: string[] = [];
  for (const [index, job] of jobs.entries()) {
    onProgress(index, jobs.length);
    await nextTask();
    throwIfAborted(signal);
    const label = reportChartLabel(job.chartId);
    try {
      const options = job.build();
      if (!options) {
        warnings.push(reportLabels.chart_unavailable(label));
        continue;
      }
      const svg = renderChartSvg(options);
      // The server prefixes the ids with the chart rank, at most this index: the estimate can only be higher.
      const stats = measureSvg(svg, reportChartIdPrefix(index));
      if (!stats) {
        warnings.push(reportLabels.chart_render_failed(label));
      } else if (exceedsSvgLimits(stats)) {
        warnings.push(reportLabels.chart_too_large(label));
      } else {
        charts.push({ submission: { package: job.package, chart_id: job.chartId, input_hash: job.inputHash, title: label, svg }, stats });
        if (stats.droppedPaths > 0) warnings.push(reportLabels.chart_degraded(label));
      }
    } catch (error) {
      console.error(`Report chart ${job.chartId} could not be rendered`, error);
      warnings.push(reportLabels.chart_render_failed(label));
    }
  }
  onProgress(jobs.length, jobs.length);
  return { charts, warnings };
};
